import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import TableComponents from 'components/Table';
import api from 'api';
import { errHelper } from 'utils';
import { formatPercent, formatCurrency, unFormatCurrency } from 'utils';
import { TooltipIcon } from 'components/Base';
import EditablePercent from 'components/Base/EditablePercent/EditablePercent';
import { getOverrideValue } from 'components/Policies/utils';
import Fund from 'components/Forms/ExistingPolicies/ManagePolicies/Funds/Fund';
import { secondaryText } from 'utils/colors';

const { TableRow, TableCell } = TableComponents;

const useStyles = makeStyles({
  greyCell: {
    color: secondaryText
  },
  indent: {
    paddingLeft: '2rem'
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  }
});

interface Props {
  id: string | number;
  fundAllocations?: any;
  overrides?: any;
  modelPortfolioFundValue?: any;
  showCharges?: boolean;
  showPerformance?: boolean;
  showAllocation?: boolean;
  withEmptyColumn?: boolean;
  sourceValue?: 1 | 2;
  baseName?: string | null;
  formName?: string | null;
  isReadOnly?: boolean;
  overrideDivideBy?: number;
  nested?: boolean;
  subTotalValues?: any;
  isEvalue?: boolean;
}

const ModelPortfolioAllocations = ({
  id,
  fundAllocations,
  overrides = [],
  modelPortfolioFundValue,
  showCharges = true,
  showPerformance = true,
  showAllocation = true,
  withEmptyColumn = false,
  sourceValue,
  baseName = null,
  formName = null,
  isReadOnly = false,
  overrideDivideBy = 100,
  nested = false,
  subTotalValues = {},
  isEvalue = false
}: Props) => {
  const [fundValue, setFundValue] = useState('');
  const [isFetchingFundsAlloactions, setIsFetchingFundsAllocations] =
    useState(false);
  const [funds, setFunds] = useState(fundAllocations || []);
  const classes = useStyles();

  const getOverrideIndex = (fund: any) => {
    return overrides.findIndex((override: any) => {
      return override.fund?.id === fund.id;
    });
  };

  useEffect(() => {
    setFundValue(modelPortfolioFundValue);
    // eslint-disable-next-line
  }, [modelPortfolioFundValue]);

  useEffect(() => {
    setFunds(fundAllocations);
  }, [fundAllocations]);

  useEffect(() => {
    if (!fundAllocations) {
      setIsFetchingFundsAllocations(true);
      api
        .getModelPortfolio({ modelPortfolioId: id })
        .then(({ data }: any) => {
          const { fund_allocations } = data;
          setFunds(fund_allocations);

          setIsFetchingFundsAllocations(false);
        })
        .catch((err: any) => {
          setIsFetchingFundsAllocations(false);
          errHelper(err);
        });
    }
    // eslint-disable-next-line
  }, []);
  return (
    <>
      {isFetchingFundsAlloactions ? (
        <TableRow>
          <TableCell colSpan={11}>loading</TableCell>
        </TableRow>
      ) : (
        funds.map((fundAllocation: any, index: number) => {
          const {
            name,
            active: isFundActive,
            sector_name,
            ocf,
            trading_costs,
            one_y_performace,
            three_y_performace,
            five_y_performace
          } = fundAllocation.fund || fundAllocation.model_portfolio_definition;

          const overrideIndex = fundAllocation.fund
            ? getOverrideIndex(fundAllocation.fund)
            : index;

          const isModelPortfolio = !!fundAllocation.model_portfolio_definition;

          const { allocation, fund_value } = fundAllocation;
          const fund =
            sourceValue === 1 && fund_value
              ? fund_value
              : allocation * Number(unFormatCurrency(fundValue));

          let fundOcf = ocf;
          let fundTradingCosts = trading_costs;
          if (overrides.length && overrides[overrideIndex]) {
            fundOcf = overrides[overrideIndex].ocf
              ? getOverrideValue(overrides[overrideIndex].ocf, overrideDivideBy)
              : ocf;

            fundTradingCosts = overrides[overrideIndex].trading_costs
              ? getOverrideValue(
                  overrides[overrideIndex].trading_costs,
                  overrideDivideBy
                )
              : trading_costs;
          }

          const totalFundCharge =
            Number(fundTradingCosts) + Number(fundOcf) || 0;

          if (isModelPortfolio) {
            return (
              <Fund
                fund={baseName}
                key={index}
                formName={formName}
                fundId={index}
                // removeFund={removeFund}
                allowPercentage={false}
                fundValues={{
                  ...fundAllocation,
                  fund_value:
                    Number(fundAllocation.allocation) *
                    Number(unFormatCurrency(modelPortfolioFundValue)),
                  model_portfolio: fundAllocation.model_portfolio_definition,
                  editable: false,
                  fund_charge_overrides: overrides
                }}
                percentageValue={formatPercent(allocation * 100)}
                subTotalValues={subTotalValues}
                isWrapCashAlone={false}
                aggregateOcfChargeValue={
                  fundAllocation.model_portfolio_definition.ocf
                }
                aggregateFundChargeValue={
                  fundAllocation.model_portfolio_definition.trading_costs
                }
                aggregateTotalFundChargeValue={
                  fundAllocation.model_portfolio_definition.total_fund_charge
                }
                aggregateOneYPerformanceValue={
                  fundAllocation.model_portfolio_definition.one_y_performace
                }
                aggregateThreeYPerformanceValue={
                  fundAllocation.model_portfolio_definition.three_y_performace
                }
                aggregateFiveYPerformanceValue={
                  fundAllocation.model_portfolio_definition.five_y_performace
                }
                isIncomplete={false}
                setIsManualFundModalOpen={() => {
                  //
                }}
                setReplicateFundFromInvalid={() => {
                  //
                }}
                removeInvalidFund={() => {
                  //
                }}
                showPerformance={showPerformance}
                showAllocation={showAllocation}
                withEmptyColumn={withEmptyColumn}
                overrideDivideBy={overrideDivideBy}
              />
            );
          }

          return (
            <TableRow key={`${index}-${fundAllocation.id}`}>
              <TableCell style={{ width: '50px' }}></TableCell>
              <TableCell
                className={`${classes.greyCell} ${nested ? classes.indent : ''}`}
              >
                {' '}
                {name}
              </TableCell>
              <TableCell className={classes.greyCell}>{sector_name}</TableCell>
              {showAllocation ||
                (isEvalue && (
                  <TableCell className={classes.greyCell}>
                    {formatCurrency(fund)}
                  </TableCell>
                ))}
              {!isEvalue && (
                <>
                  <TableComponents.TableCell className={classes.greyCell}>
                    -
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.greyCell}>
                    -
                  </TableComponents.TableCell>
                </>
              )}
              <TableCell className={classes.greyCell}>
                {formatPercent(allocation * 100)}
              </TableCell>
              {showCharges && (
                <>
                  <TableCell className={classes.greyCell}>
                    {formName && !isReadOnly ? (
                      <EditablePercent
                        name={`${baseName}.fund_charge_overrides[${overrideIndex}].ocf`}
                        formName={formName}
                        defaultValue={ocf}
                      />
                    ) : (
                      <>{formatPercent(Number(fundOcf) * 100)}</>
                    )}
                  </TableCell>
                  <TableCell className={classes.greyCell}>
                    {formName && !isReadOnly ? (
                      <EditablePercent
                        name={`${baseName}.fund_charge_overrides[${overrideIndex}].trading_costs`}
                        formName={formName}
                        defaultValue={trading_costs}
                      />
                    ) : (
                      <>{formatPercent(Number(fundTradingCosts) * 100)}</>
                    )}
                  </TableCell>
                  <TableCell className={classes.greyCell}>
                    {formatPercent(totalFundCharge * 100)}
                  </TableCell>
                </>
              )}
              {showPerformance && (
                <>
                  <TableCell className={classes.greyCell}>
                    {one_y_performace
                      ? formatPercent(Number(one_y_performace) * 100, true)
                      : '-'}
                  </TableCell>
                  <TableCell className={classes.greyCell}>
                    {three_y_performace
                      ? formatPercent(Number(three_y_performace) * 100, true)
                      : '-'}
                  </TableCell>
                  <TableCell className={classes.greyCell}>
                    {five_y_performace
                      ? formatPercent(Number(five_y_performace) * 100, true)
                      : '-'}
                  </TableCell>
                </>
              )}
              <TableCell>
                <div className={classes.info}>
                  {fundAllocation.fund && !isFundActive && (
                    <TooltipIcon
                      text="Fund are no longer supported."
                      icon="warn"
                    />
                  )}
                </div>
              </TableCell>
              {withEmptyColumn && (
                <>
                  <TableCell />
                  <TableCell />
                </>
              )}
            </TableRow>
          );
        })
      )}
    </>
  );
};

export default ModelPortfolioAllocations;
